// =============================================================
// Lab page interactive components
// =============================================================

const { useState: labUS, useEffect: labUE, useRef: labUR } = React;

// ---------- Interactive Perio Chart ----------
const TEETH = [2,3,4,5,6,7,8,9,10,11,12,13,14,15];
const SITES = ['MB', 'B', 'DB']; // buccal aspect for clarity

function depthClass(d) {
  if (!d) return '';
  if (d >= 6) return 'd-danger';
  if (d >= 4) return 'd-warn';
  return 'd-mild';
}

// initial sample chart - looks "real"
function initChart() {
  const chart = {};
  TEETH.forEach(t => {
    SITES.forEach(s => {
      chart[t + '-' + s] = { depth: 0, bop: false };
    });
  });
  // seed some readings to look populated
  const seeded = [
    ['3','MB',3], ['3','B',2], ['3','DB',3],
    ['4','MB',2], ['4','B',2], ['4','DB',3],
    ['5','MB',4], ['5','B',3], ['5','DB',4,true],
    ['6','MB',3], ['6','B',2], ['6','DB',3],
    ['14','MB',3], ['14','B',2], ['14','DB',6,true],
    ['15','MB',8,true], ['15','B',4], ['15','DB',5,true],
  ];
  seeded.forEach(([t,s,d,bop]) => { chart[t+'-'+s] = { depth: d, bop: !!bop }; });
  return chart;
}

function PerioChart() {
  const [chart, setChart] = labUS(initChart);
  const [mode, setMode] = labUS('depth'); // depth | bop
  const [recent, setRecent] = labUS(null);
  const [recording, setRecording] = labUS(false);

  const cycleDepth = (key) => {
    setChart(c => ({ ...c, [key]: { ...c[key], depth: ((c[key].depth || 0) % 9) + 1 } }));
    setRecent(key);
    setTimeout(() => setRecent(r => r === key ? null : r), 900);
  };
  const toggleBop = (key) => {
    setChart(c => ({ ...c, [key]: { ...c[key], bop: !c[key].bop } }));
    setRecent(key);
    setTimeout(() => setRecent(r => r === key ? null : r), 900);
  };

  const onCellClick = (key) => {
    if (mode === 'depth') cycleDepth(key);
    else toggleBop(key);
  };

  // Simulated voice progression
  labUE(() => {
    if (!recording) return undefined;
    const SCRIPT = [
      ['7','MB',3], ['7','B',2], ['7','DB',3],
      ['8','MB',2], ['8','B',2], ['8','DB',2],
      ['9','MB',2], ['9','B',2], ['9','DB',3],
      ['10','MB',3], ['10','B',3], ['10','DB',4,true],
      ['11','MB',5], ['11','B',3], ['11','DB',6,true],
      ['12','MB',4,true], ['12','B',3], ['12','DB',5,true],
      ['13','MB',6,true], ['13','B',4], ['13','DB',7,true],
    ];
    let i = 0;
    const id = setInterval(() => {
      if (i >= SCRIPT.length) { setRecording(false); return; }
      const [t,s,d,bop] = SCRIPT[i++];
      const key = t + '-' + s;
      setChart(c => ({ ...c, [key]: { depth: d, bop: !!bop } }));
      setRecent(key);
    }, 380);
    return () => clearInterval(id);
  }, [recording]);

  // Stats
  const entries = Object.values(chart);
  const populated = entries.filter(e => e.depth > 0);
  const overFour = populated.filter(e => e.depth >= 4).length;
  const overSix = populated.filter(e => e.depth >= 6).length;
  const bopCount = populated.filter(e => e.bop).length;
  const bopPct = populated.length ? Math.round(bopCount / populated.length * 100) : 0;
  const meanDepth = populated.length ? (populated.reduce((s,e) => s+e.depth, 0)/populated.length).toFixed(1) : '0.0';

  return (
    <div className="perio-shell">
      <div className="perio-toolbar">
        <div style={{display:'flex', alignItems:'center', gap:18, flexWrap:'wrap'}}>
          <div style={{fontWeight:600, fontSize:14}}>Periodontal · Maxillary</div>
          {recording ? (
            <span className="perio-rec"><span className="perio-rec-dot"></span> Listening · "Tooth thirteen, distal-buccal…"</span>
          ) : (
            <span style={{fontSize:12.5, color:'var(--text-soft)'}}>Click cells to chart by hand, or hit record to chart by voice.</span>
          )}
        </div>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <div className="perio-mode" role="tablist">
            <button className={mode==='depth' ? 'active' : ''} onClick={() => setMode('depth')}>Depth</button>
            <button className={mode==='bop' ? 'active' : ''} onClick={() => setMode('bop')}>BOP</button>
          </div>
          <button className={"btn btn-sm " + (recording ? "btn-ghost" : "btn-primary")}
                  onClick={() => setRecording(r => !r)}>
            {recording ? '◼ Stop' : '● Record voice'}
          </button>
        </div>
      </div>

      <div className="perio-grid">
        {/* Tooth visual row */}
        <div className="perio-tooth-row">
          <div className="perio-row-label">Tooth</div>
          {TEETH.map(t => (
            <div key={t} className="perio-tooth">
              <ToothIcon n={t} />
              <div style={{marginTop:2}}>#{t}</div>
            </div>
          ))}
        </div>

        {SITES.map(site => (
          <div className="perio-row" key={site}>
            <div className="perio-row-label">{site === 'MB' ? 'Mesio-Buccal' : site === 'B' ? 'Buccal' : 'Disto-Buccal'}</div>
            {TEETH.map(t => {
              const key = t + '-' + site;
              const cell = chart[key];
              const cls = ['perio-cell', depthClass(cell.depth)];
              if (cell.bop) cls.push('bop');
              if (recent === key) cls.push('recent');
              return (
                <button key={key} className={cls.join(' ')}
                        data-depth={cell.depth}
                        onClick={() => onCellClick(key)}
                        aria-label={`Tooth ${t} ${site}: ${cell.depth || 'empty'}${cell.bop ? ', BOP' : ''}`}>
                  {cell.depth || '·'}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="perio-stats">
        <div className="perio-stat-card">
          <div className="perio-stat-label">Sites &gt; 4mm</div>
          <div className="perio-stat-val">{overFour}<span className="unit">/ {populated.length || 1}</span></div>
          <div className="perio-stat-bar"><span style={{width: (populated.length ? overFour/populated.length*100 : 0) + '%', background:'var(--warning)'}}></span></div>
        </div>
        <div className="perio-stat-card">
          <div className="perio-stat-label">Sites ≥ 6mm</div>
          <div className="perio-stat-val">{overSix}<span className="unit">deep</span></div>
          <div className="perio-stat-bar"><span style={{width: (populated.length ? overSix/populated.length*100 : 0) + '%', background:'var(--danger)'}}></span></div>
        </div>
        <div className="perio-stat-card">
          <div className="perio-stat-label">BOP percentage</div>
          <div className="perio-stat-val">{bopPct}<span className="unit">%</span></div>
          <div className="perio-stat-bar"><span style={{width: bopPct + '%'}}></span></div>
        </div>
        <div className="perio-stat-card">
          <div className="perio-stat-label">Mean depth</div>
          <div className="perio-stat-val">{meanDepth}<span className="unit">mm</span></div>
          <div className="perio-stat-bar"><span style={{width: Math.min(100, (Number(meanDepth)/8)*100) + '%'}}></span></div>
        </div>
      </div>
    </div>
  );
}

function ToothIcon({ n }) {
  // Simple stylized tooth - molar shape for back teeth (1-3, 14-16), bicuspid for 4-5, 12-13, etc.
  const isMolar = n <= 3 || n >= 14;
  const isCanine = n === 6 || n === 11;
  return (
    <svg viewBox="0 0 24 28" width="22" height="26" aria-hidden="true">
      <path
        d={isMolar
          ? "M5 5 C 4 2 8 1 12 1 C 16 1 20 2 19 5 C 19 8 21 13 19 18 C 18 22 17 27 15 27 C 13 27 13 22 12 22 C 11 22 11 27 9 27 C 7 27 6 22 5 18 C 3 13 5 8 5 5 Z"
          : isCanine
            ? "M7 5 C 6 2 10 1 12 1 C 14 1 18 2 17 5 C 17 9 19 15 17 20 C 16 25 14 27 12 27 C 10 27 8 25 7 20 C 5 15 7 9 7 5 Z"
            : "M6 5 C 5 2 9 1 12 1 C 15 1 19 2 18 5 C 18 9 20 14 18 19 C 17 23 15 27 12 27 C 9 27 7 23 6 19 C 4 14 6 9 6 5 Z"}
        fill="#fff"
        stroke="#94A3B8"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------- Lab UI Mock ----------
function LabUI() {
  const navItem = (icon, label, opts={}) => (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      padding:'9px 12px', borderRadius:8,
      fontSize:13,
      color: opts.active ? '#fff' : 'rgba(255,255,255,0.78)',
      background: opts.active ? 'rgba(255,255,255,0.08)' : 'transparent',
      cursor:'pointer',
    }}>
      <span style={{width:16, display:'inline-flex', justifyContent:'center', opacity:0.85}}>{icon}</span>
      <span style={{flex:1}}>{label}</span>
      {opts.badge && (
        <span style={{
          fontSize:11, padding:'1px 7px', borderRadius:10,
          background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.65)',
        }}>{opts.badge}</span>
      )}
    </div>
  );

  const sectionHeader = (label) => (
    <div style={{
      fontSize:10.5, letterSpacing:'0.1em', textTransform:'uppercase',
      color:'rgba(255,255,255,0.4)', fontWeight:600,
      margin:'18px 12px 6px',
    }}>{label}</div>
  );

  const tab = (label, active=false, badge=null) => (
    <div style={{
      padding:'10px 2px',
      fontSize:13.5, fontWeight: active ? 600 : 500,
      color: active ? 'var(--teal-deep)' : 'var(--text-soft)',
      borderBottom: active ? '2px solid var(--teal)' : '2px solid transparent',
      display:'inline-flex', alignItems:'center', gap:6,
      cursor:'pointer',
    }}>
      {label}
      {badge && <span style={{
        fontSize:10.5, padding:'1px 6px', borderRadius:10,
        background:'var(--teal-wash)', color:'var(--teal-deep)', fontWeight:600,
      }}>{badge}</span>}
    </div>
  );

  const extractField = (label) => (
    <div style={{
      background:'#fff', border:'1px solid var(--border)', borderRadius:8,
      padding:'8px 12px', display:'flex', flexDirection:'column', gap:2,
    }}>
      <div style={{fontSize:11.5, color:'var(--text-soft)'}}>{label}</div>
      <div style={{fontSize:13, color:'var(--text-muted)'}}>-</div>
    </div>
  );

  return (
    <div className="lab-ui" style={{height:'auto', minHeight:680}}>
      {/* ============ LEFT RAIL ============ */}
      <aside className="lab-ui-rail" style={{padding:'14px 12px', justifyContent:'space-between'}}>
        <div style={{display:'flex', flexDirection:'column', gap:2}}>
          {/* Brand */}
          <div style={{display:'flex', alignItems:'center', gap:8, padding:'4px 8px 14px'}}>
            <div style={{
              width:26, height:26, borderRadius:7,
              background:'linear-gradient(135deg, #19ADC5, #0F7689)',
              display:'grid', placeItems:'center', color:'#fff', fontWeight:700, fontSize:14,
            }}>D</div>
            <div style={{fontWeight:700, fontSize:14, color:'#fff', letterSpacing:0.2}}>DENTICODE</div>
            <span style={{
              marginLeft:'auto', fontSize:9.5, fontWeight:700, padding:'2px 6px',
              borderRadius:4, background:'rgba(25,173,197,0.18)', color:'#6BD7E8', letterSpacing:'0.1em',
            }}>PRO</span>
          </div>

          {/* Practice switcher */}
          <div style={{
            background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.06)',
            borderRadius:10, padding:'10px 12px', display:'flex', alignItems:'center', gap:10,
            marginBottom:10,
          }}>
            <div style={{
              width:28, height:28, borderRadius:8,
              background:'var(--teal-wash)', color:'var(--teal-deep)',
              display:'grid', placeItems:'center', fontWeight:700, fontSize:11,
            }}>CD</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:12.5, fontWeight:600, color:'#fff'}}>Costal Dental</div>
              <div style={{fontSize:11, color:'rgba(255,255,255,0.5)'}}>1 location · 1 user</div>
              <div style={{fontSize:10.5, color:'rgba(255,255,255,0.4)', marginTop:2}}>📍 All locations</div>
            </div>
          </div>

          {sectionHeader('Workflow')}
          {navItem('▦', 'Dashboard')}
          {navItem('👥', 'Patients', {badge:'20'})}
          {navItem('🦷', 'Denticode Lab', {active:true})}
          {navItem('📁', 'Files')}
          {navItem('🕘', 'History')}

          {sectionHeader('Toolkit')}
          {navItem('✦', 'Assistant')}
          {navItem('⌗', 'Code Library')}
          {navItem('▤', 'Templates')}
          {navItem('🗓', 'Scheduler')}
          {navItem('📊', 'Analytics')}
          {navItem('?', 'Support')}
          {navItem('⚙', 'Settings')}
        </div>

        {/* Bottom: HIPAA + user */}
        <div style={{display:'flex', flexDirection:'column', gap:10, paddingTop:10}}>
          <div style={{
            background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.15)',
            borderRadius:8, padding:'8px 10px',
          }}>
            <div style={{fontSize:10.5, fontWeight:700, color:'#86EFAC', letterSpacing:'0.06em'}}>🔒 HIPAA SECURED</div>
            <div style={{fontSize:10.5, color:'rgba(255,255,255,0.5)', marginTop:2}}>Encrypted in transit &amp; at rest</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8, padding:'4px 4px'}}>
            <div style={{
              width:28, height:28, borderRadius:'50%',
              background:'linear-gradient(135deg,#19ADC5,#0F7689)',
              color:'#fff', display:'grid', placeItems:'center', fontWeight:700, fontSize:11,
            }}>RJ</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:12.5, fontWeight:600, color:'#fff'}}>Ryan Junt</div>
              <div style={{fontSize:10.5, color:'rgba(255,255,255,0.45)'}}>Super Admin</div>
            </div>
          </div>
          <div style={{fontSize:10, color:'rgba(255,255,255,0.3)', paddingLeft:4}}>v0.1.0</div>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <main className="lab-ui-main">
        {/* Top bar */}
        <div className="lab-ui-topbar" style={{padding:'10px 22px'}}>
          <div style={{fontWeight:600, fontSize:14}}>Denticode Lab</div>
          <div style={{
            flex:1, maxWidth:520, margin:'0 18px',
            display:'flex', alignItems:'center', gap:8,
            background:'var(--surface-subtle)', border:'1px solid var(--border)',
            borderRadius:10, padding:'7px 12px',
          }}>
            <span style={{color:'var(--text-soft)', fontSize:13}}>🔍</span>
            <span style={{flex:1, fontSize:13, color:'var(--text-soft)'}}>Search patients, codes, sessions…</span>
            <span style={{
              fontSize:11, fontFamily:'var(--font-mono)',
              color:'var(--text-soft)', padding:'1px 6px', border:'1px solid var(--border)',
              borderRadius:5, background:'#fff',
            }}>⌘K</span>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:14, color:'var(--text-soft)', fontSize:14}}>
            <span>🔔</span><span>?</span><span>🔒</span>
          </div>
        </div>

        {/* Patient header */}
        <div style={{padding:'18px 22px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12}}>
          <div style={{display:'flex', gap:12, alignItems:'flex-start'}}>
            <div style={{
              width:40, height:40, borderRadius:'50%',
              background:'var(--teal-wash)', color:'var(--teal-deep)',
              display:'grid', placeItems:'center', fontWeight:700, fontSize:13,
            }}>CS</div>
            <div>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <div style={{fontWeight:700, fontSize:18}}>Catherine Stone</div>
                <span style={{
                  fontSize:11, padding:'2px 8px', borderRadius:5,
                  background:'var(--surface-muted)', color:'var(--text-muted)',
                  fontFamily:'var(--font-mono)', fontWeight:600,
                }}>ready_for_billing</span>
              </div>
              <div style={{fontSize:12, color:'var(--text-soft)', marginTop:3}}>
                Crown Preparation Visit Note · Created May 15, 5:38 PM · ID ba23f7e0 · MRN 12000019226989
              </div>
            </div>
          </div>
          <a href="#" style={{fontSize:13, color:'var(--text-muted)', textDecoration:'none'}}>← Back to Lab</a>
        </div>

        {/* Tabs */}
        <div style={{
          padding:'8px 22px 0', borderBottom:'1px solid var(--border)',
          display:'flex', gap:22,
        }}>
          {tab('Overview', true)}
          {tab('Recordings', false, '1')}
          {tab('Billing')}
          {tab('Note')}
          {tab('Perio')}
          {tab('Raw')}
        </div>

        {/* Body */}
        <div className="lab-ui-body" style={{padding:'18px 22px', gap:14}}>
          {/* Ready to record card */}
          <div style={{
            background:'#fff', border:'1px solid var(--border)', borderRadius:12,
            padding:'16px 18px', display:'flex', alignItems:'center', gap:14,
          }}>
            <div style={{
              width:38, height:38, borderRadius:10,
              background:'var(--teal-wash)', color:'var(--teal-deep)',
              display:'grid', placeItems:'center', fontSize:16,
            }}>🎙</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:600, fontSize:14}}>Ready to record</div>
              <div style={{fontSize:12.5, color:'var(--text-soft)', marginTop:2}}>
                Click Start recording to capture audio with your microphone, or upload a file you already have.
              </div>
            </div>
            <button className="btn btn-sm" style={{
              background:'#fff', border:'1px solid var(--border)', color:'var(--text)',
              fontSize:12.5, padding:'7px 14px',
            }}>⬆ Upload audio</button>
            <button className="btn btn-sm btn-primary" style={{fontSize:12.5, padding:'7px 14px'}}>
              🎙 Start recording
            </button>
          </div>

          {/* Artifacts */}
          <div style={{
            background:'#fff', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px',
          }}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <span style={{fontSize:11, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em'}}>📎 ARTIFACTS</span>
                <span style={{
                  fontSize:11, padding:'1px 7px', borderRadius:5,
                  background:'var(--surface-muted)', color:'var(--text-muted)', fontWeight:600,
                }}>1 / 1 included</span>
              </div>
              <div style={{display:'flex', gap:14, fontSize:12, color:'var(--text-soft)'}}>
                <span>☐ Exclude all</span>
                <span>👁 Preview what AI will see</span>
              </div>
            </div>
            <div style={{fontSize:11.5, color:'var(--text-soft)', marginBottom:10, lineHeight:1.5}}>
              Tick / untick artifacts to control what the template-fill, billing-code, and narrative generators read.
            </div>
            <div style={{
              display:'flex', alignItems:'center', gap:10,
              border:'1px solid var(--border)', borderRadius:8, padding:'9px 12px',
              background:'var(--surface-subtle)',
            }}>
              <span style={{
                width:18, height:18, borderRadius:4,
                background:'var(--teal)', color:'#fff',
                display:'grid', placeItems:'center', fontSize:11,
              }}>✓</span>
              <span style={{color:'var(--teal-deep)'}}>▶</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13, fontWeight:500}}>denticode_recording_1778940698951.wav</div>
                <div style={{fontSize:11, color:'var(--text-soft)'}}>Transcript completed</div>
              </div>
            </div>
            <button className="btn btn-sm" style={{
              marginTop:10, background:'#fff', border:'1px solid var(--border)', color:'var(--text)',
              fontSize:12, padding:'6px 12px',
            }}>✦ Refresh AI from all artifacts</button>
          </div>

          {/* Billing codes */}
          <div style={{
            background:'#fff', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px',
          }}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:11, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em'}}>$ BILLING CODES</span>
                <span style={{fontSize:11, padding:'1px 7px', borderRadius:5, background:'var(--teal-wash)', color:'var(--teal-deep)', fontWeight:600}}>1 billable</span>
                <span style={{fontSize:11, padding:'1px 7px', borderRadius:5, background:'#FEF3C7', color:'#B45309', fontWeight:600}}>1 needs evidence</span>
              </div>
              <a href="#" style={{fontSize:12, color:'var(--text-muted)', textDecoration:'none'}}>See the Billing tab for full detail.</a>
            </div>
            <div style={{display:'flex', alignItems:'center', gap:10, fontSize:13}}>
              <span style={{
                fontFamily:'var(--font-mono)', fontWeight:600, padding:'3px 8px',
                background:'var(--teal-wash)', color:'var(--teal-deep)', borderRadius:5,
              }}>D0220</span>
              <span style={{flex:1}}>Intraoral periapical first radiographic image</span>
              <span style={{fontFamily:'var(--font-mono)', color:'var(--text-soft)', fontSize:12}}>#19</span>
              <span style={{fontSize:10.5, padding:'2px 7px', borderRadius:4, background:'var(--teal-wash)', color:'var(--teal-deep)', fontWeight:700}}>AI</span>
              <span style={{fontSize:11, padding:'2px 7px', borderRadius:4, background:'#DCFCE7', color:'#15803D', fontWeight:700}}>93%</span>
            </div>
          </div>

          {/* Required attachments */}
          <div style={{
            background:'#fff', border:'1px solid var(--border)', borderRadius:12, padding:'14px 18px',
          }}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:11, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em'}}>📎 REQUIRED ATTACHMENTS</span>
                <span style={{fontSize:11, padding:'1px 7px', borderRadius:5, background:'#FEE2E2', color:'#B91C1C', fontWeight:600}}>0 of 5 resolved</span>
              </div>
              <span style={{fontSize:12, color:'var(--text-soft)'}}>Based on this lab's codes.</span>
            </div>
            <div style={{
              border:'1px solid #FCA5A5', background:'#FEF2F2', borderRadius:10, padding:'12px 14px',
              display:'flex', alignItems:'flex-start', gap:10,
            }}>
              <span style={{color:'#B91C1C', fontSize:16, lineHeight:1}}>⊘</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13, color:'var(--text)', lineHeight:1.5}}>
                  A focused X-ray showing the entire tooth from crown to root apex, used to evaluate the root structure and surrounding bone prior to treatment.
                </div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:6}}>Pre-operative periapical radiograph of the full tooth structure</div>
                <div style={{fontSize:11.5, color:'var(--text-soft)', marginTop:4}}>
                  Triggered by <span style={{fontFamily:'var(--font-mono)', padding:'1px 6px', background:'#fff', border:'1px solid var(--border)', borderRadius:4, color:'var(--teal-deep)'}}>D2740</span>
                </div>
                <div style={{display:'flex', gap:8, marginTop:10}}>
                  <button className="btn btn-sm" style={{background:'#fff', border:'1px solid var(--border)', color:'var(--text)', fontSize:12, padding:'5px 11px'}}>⬆ Upload file</button>
                  <button className="btn btn-sm btn-primary" style={{fontSize:12, padding:'5px 11px'}}>✓ Mark complete</button>
                </div>
              </div>
              <span style={{
                fontSize:10.5, padding:'2px 7px', borderRadius:4,
                background:'#B91C1C', color:'#fff', fontWeight:700, letterSpacing:'0.04em',
              }}>Required</span>
            </div>
          </div>
        </div>
      </main>

      {/* ============ RIGHT RAIL ============ */}
      <aside className="lab-ui-aside" style={{padding:'18px 18px', gap:14}}>
        {/* Note template */}
        <div style={{
          background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px',
        }}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8}}>
            <span style={{fontSize:10.5, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.1em'}}>▤ NOTE TEMPLATE</span>
            <span style={{fontSize:11, color:'var(--text-soft)', fontFamily:'var(--font-mono)'}}>0%</span>
          </div>
          <div style={{
            border:'1px solid var(--border)', borderRadius:8, padding:'9px 12px',
            display:'flex', alignItems:'center', justifyContent:'space-between',
            background:'var(--surface-subtle)', fontSize:13, color:'var(--text-muted)',
          }}>
            No template selected
            <span style={{color:'var(--text-soft)'}}>▾</span>
          </div>
          <div style={{height:6, background:'var(--surface-muted)', borderRadius:3, marginTop:10}}>
            <div style={{width:'0%', height:'100%', background:'var(--teal)', borderRadius:3}}/>
          </div>
          <div style={{fontSize:11, color:'var(--text-soft)', marginTop:6}}>0 of 8 filled · 0 locked by you</div>
        </div>

        {/* Auto-extracted */}
        <div style={{
          background:'#fff', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px',
          display:'flex', flexDirection:'column', gap:12,
        }}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <span style={{fontSize:10.5, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.1em'}}>✦ AUTO-EXTRACTED</span>
            <div style={{display:'flex', gap:6}}>
              <span style={{fontSize:10, padding:'2px 6px', borderRadius:4, background:'var(--teal-wash)', color:'var(--teal-deep)', fontWeight:600}}>Watching transcript</span>
              <span style={{fontSize:10, padding:'2px 6px', borderRadius:4, background:'var(--surface-muted)', color:'var(--text-muted)', fontWeight:600}}>AI idle</span>
            </div>
          </div>

          <div>
            <div style={{fontSize:10.5, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em', marginBottom:6}}>PROCEDURE</div>
            <div style={{display:'flex', flexDirection:'column', gap:6}}>
              {extractField('Tooth')}
              {extractField('Surfaces')}
            </div>
          </div>

          <div>
            <div style={{fontSize:10.5, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em', marginBottom:6}}>ANESTHESIA</div>
            <div style={{display:'flex', flexDirection:'column', gap:6}}>
              {extractField('Anesthetic')}
              {extractField('Carpules')}
              {extractField('Epi ratio')}
            </div>
          </div>

          <div>
            <div style={{fontSize:10.5, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em', marginBottom:6}}>MATERIALS</div>
            {extractField('Shade')}
          </div>

          <div>
            <div style={{fontSize:10.5, fontWeight:700, color:'var(--text-soft)', letterSpacing:'0.08em', marginBottom:6}}>IMAGING</div>
            {extractField('Imaging')}
          </div>
        </div>
      </aside>
    </div>
  );
}

// ---------- Template editor ----------
function TemplateEditor() {
  return (
    <div className="tmpl-editor">
      <span style={{color:'var(--text-soft)'}}>Template name:</span> <strong>Perio SRP Re-Evaluation</strong>{"\n\n"}
      Periodontal Re-Evaluation · <span className="tmpl-token filled">Upper Right</span>{"\n"}
      ═══════════════════════════════════════════{"\n"}
      Chief complaint:&nbsp;&nbsp;&nbsp;&nbsp;<span className="tmpl-token filled">Roughness on upper teeth</span>{"\n"}
      Diagnosis:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="tmpl-token filled">Localized periodontitis, UR</span>{"\n\n"}
      Findings:{"\n"}
      &nbsp;&nbsp;• #<span className="tmpl-token filled">14</span> <span className="tmpl-token filled">DL</span> - <span className="tmpl-token filled">6mm</span>, BOP+{"\n"}
      &nbsp;&nbsp;• #<span className="tmpl-token filled">15</span> <span className="tmpl-token filled">M</span>  - <span className="tmpl-token filled">8mm</span>, BOP+, mobility 1{"\n\n"}
      Treatment performed:{"\n"}
      &nbsp;&nbsp;• <span className="tmpl-token">#!cdt_code!#</span>{"  "}<span className="tmpl-token">#!cdt_description!#</span>{"\n"}
      &nbsp;&nbsp;• <span className="tmpl-token filled">D9215</span>{"  "}<span className="tmpl-token filled">Local anesthesia</span>{"\n\n"}
      Provider:{"  "}<span className="tmpl-token filled">Dr. S. Patel</span>
    </div>
  );
}

// ---------- Analytics ----------
function Analytics() {
  // little sparklines
  const spark1 = "M0,30 L10,25 L20,28 L30,18 L40,22 L50,16 L60,18 L70,10 L80,14 L90,8 L100,12";
  const spark2 = "M0,20 L10,22 L20,18 L30,24 L40,20 L50,18 L60,12 L70,16 L80,10 L90,14 L100,8";
  return (
    <div className="analytics-cards">
      <div className="analytics-card">
        <div className="analytics-card-l">Sites &gt; 4mm</div>
        <div className="analytics-card-v">28<span style={{fontSize:14, color:'var(--text-muted)', fontWeight:500}}> / 84</span></div>
        <svg viewBox="0 0 100 32" preserveAspectRatio="none"><path d={spark1} fill="none" stroke="#19ADC5" strokeWidth="2" strokeLinecap="round" /></svg>
      </div>
      <div className="analytics-card">
        <div className="analytics-card-l">BOP %</div>
        <div className="analytics-card-v">34<span style={{fontSize:14, color:'var(--text-muted)', fontWeight:500}}>%</span></div>
        <svg viewBox="0 0 100 32" preserveAspectRatio="none"><path d={spark2} fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" /></svg>
      </div>
      <div className="analytics-card">
        <div className="analytics-card-l">Mean PD</div>
        <div className="analytics-card-v">3.4<span style={{fontSize:14, color:'var(--text-muted)', fontWeight:500}}> mm</span></div>
        <svg viewBox="0 0 100 32" preserveAspectRatio="none"><path d="M0,18 L100,14" stroke="#15803D" strokeWidth="2" /></svg>
      </div>
      <div className="analytics-card">
        <div className="analytics-card-l">Mobility &gt; 0</div>
        <div className="analytics-card-v">2<span style={{fontSize:14, color:'var(--text-muted)', fontWeight:500}}> teeth</span></div>
        <div style={{display:'flex', gap:4, marginTop:6}}>
          <span style={{flex:1, height:6, background:'var(--surface-muted)', borderRadius:3}}>
            <span style={{display:'block', width:'18%', height:'100%', background:'var(--teal)', borderRadius:3}}></span>
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------- Bilingual mock ----------
function BilingualMock() {
  return (
    <div style={{
      background:'#fff',
      border:'1px solid var(--border)',
      borderRadius:'var(--r-xl)',
      padding:'24px',
      boxShadow:'var(--shadow-md)',
      display:'grid',
      gridTemplateColumns:'1fr 1fr',
      gap: 24,
    }}>
      <div>
        <div style={{display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:14}}>
          <span style={{padding:'3px 8px', borderRadius:4, background:'var(--teal-wash)', color:'var(--teal-deep)', fontFamily:'var(--font-mono)', fontSize:11}}>ES</span>
          Speaking
        </div>
        <div style={{display:'flex', flexDirection:'column', gap:12, fontSize:13.5, lineHeight:1.5}}>
          <div><strong style={{color:'var(--teal-deep)'}}>Provider:</strong> Vamos a empezar la re-evaluación periodontal del cuadrante superior derecho.</div>
          <div><strong style={{color:'var(--text-soft)'}}>Patient:</strong> ¿Voy a estar adormecido?</div>
          <div><strong style={{color:'var(--teal-deep)'}}>Provider:</strong> Sí, anestesia local. Diente quince, mesial, ocho milímetros, sangrado.</div>
        </div>
      </div>
      <div>
        <div style={{display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600, color:'var(--text-soft)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:14}}>
          <span style={{padding:'3px 8px', borderRadius:4, background:'var(--teal-wash)', color:'var(--teal-deep)', fontFamily:'var(--font-mono)', fontSize:11}}>EN</span>
          Note
        </div>
        <div style={{
          fontFamily:'var(--font-mono)', fontSize:12.5, lineHeight:1.7,
          background:'var(--surface-subtle)', padding:14, borderRadius:10,
        }}>
          Quadrant:&nbsp;&nbsp;&nbsp;&nbsp;Upper Right<br/>
          Anesthesia:&nbsp;&nbsp;Local - bupivacaine 0.5%<br/>
          Findings:&nbsp;&nbsp;&nbsp;&nbsp;<br/>
          &nbsp;&nbsp;• #15 M - 8mm, BOP+<br/>
          Procedure:&nbsp;&nbsp;&nbsp;D4341 SRP, UR<br/>
        </div>
      </div>
    </div>
  );
}

function mountIfExists(id, el) {
  const node = document.getElementById(id);
  if (node) ReactDOM.createRoot(node).render(el);
}
mountIfExists('perio-mount', <PerioChart />);
mountIfExists('lab-ui-mount', <LabUI />);
mountIfExists('tmpl-mount', <TemplateEditor />);
mountIfExists('analytics-mount', <Analytics />);
mountIfExists('bilingual-mount', <BilingualMock />);
